import * as THREE from 'three';
import { ARENA_RADIUS, LANE_HALF_WIDTH, LANE_PATHS, getHeroDef, type Simulation, type Team } from '@moba/shared';
import { isVisible, visionSources, type VisionSource } from './FogOfWar.ts';

const TEAM_CSS: Record<Team, string> = { blue: '#4da3ff', red: '#ff5a5a' };
const SQRT1_2 = Math.SQRT1_2;

/**
 * Minimapa estilo LoL (abajo a la derecha). Usa la misma orientación que la
 * cámara isométrica: azul abajo a la izquierda, rojo arriba a la derecha.
 *  - clic derecho: mover el héroe a ese punto
 *  - mantener clic izquierdo: mirar esa zona con la cámara (`peek`)
 */
export class Minimap {
  /** punto del mundo que la cámara debe mirar mientras se mantiene el clic izquierdo */
  peek: { x: number; z: number } | null = null;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly size: number;
  private readonly scale: number;
  private background: HTMLCanvasElement;
  private backgroundLanes = '0,1,2';
  private readonly dpr: number;
  private readonly fogCanvas: HTMLCanvasElement;
  private readonly fogCtx: CanvasRenderingContext2D;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly ndc = new THREE.Vector2();
  private readonly hit = new THREE.Vector3();
  private leftDown = false;

  constructor(
    private readonly onMove: (x: number, z: number) => void,
  ) {
    const canvas = document.getElementById('minimap') as HTMLCanvasElement | null;
    if (!canvas) throw new Error('Falta #minimap');
    this.canvas = canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.size = canvas.clientWidth || 220;
    canvas.width = this.size * dpr;
    canvas.height = this.size * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D no disponible');
    this.ctx = ctx;
    ctx.scale(dpr, dpr);
    this.scale = (this.size / 2 - 6) / ARENA_RADIUS;
    this.dpr = dpr;
    this.background = this.drawBackground(dpr, [0, 1, 2]);
    this.fogCanvas = document.createElement('canvas');
    this.fogCanvas.width = canvas.width;
    this.fogCanvas.height = canvas.height;
    const fogCtx = this.fogCanvas.getContext('2d');
    if (!fogCtx) throw new Error('Canvas 2D no disponible');
    fogCtx.scale(dpr, dpr);
    this.fogCtx = fogCtx;

    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
  }

  /** mundo (x, z) -> píxel del minimapa */
  private toMap(x: number, z: number): [number, number] {
    const sx = (-x + z) * SQRT1_2;
    const sy = (x + z) * SQRT1_2;
    return [this.size / 2 + sx * this.scale, this.size / 2 - sy * this.scale];
  }

  /** píxel del minimapa -> mundo (x, z) */
  private toWorld(px: number, py: number): { x: number; z: number } {
    const sx = (px - this.size / 2) / this.scale;
    const sy = (this.size / 2 - py) / this.scale;
    return { x: (sy - sx) * SQRT1_2, z: (sy + sx) * SQRT1_2 };
  }

  private drawBackground(dpr: number, lanes: number[]): HTMLCanvasElement {
    const bg = document.createElement('canvas');
    bg.width = this.size * dpr;
    bg.height = this.size * dpr;
    const ctx = bg.getContext('2d')!;
    ctx.scale(dpr, dpr);
    const c = this.size / 2;
    ctx.fillStyle = '#0b131c';
    ctx.beginPath();
    ctx.arc(c, c, ARENA_RADIUS * this.scale, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(110, 160, 210, 0.45)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(120, 170, 220, 0.22)';
    ctx.lineWidth = LANE_HALF_WIDTH * 2 * this.scale;
    // sólo las líneas en juego (ARAM: la central)
    for (const [lane, path] of LANE_PATHS.entries()) {
      if (!lanes.includes(lane)) continue;
      ctx.beginPath();
      path.forEach((p, i) => {
        const [x, y] = this.toMap(p.x, p.z);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
    return bg;
  }

  update(sim: Simulation, camera: THREE.Camera): void {
    const ctx = this.ctx;
    const player = sim.player;
    ctx.clearRect(0, 0, this.size, this.size);
    // el fondo (con las líneas en juego) se rehace al cambiar de modo
    const lanes = sim.laneEnabled ? sim.activeLanes : [0, 1, 2];
    const key = lanes.join(',');
    if (key !== this.backgroundLanes) {
      this.backgroundLanes = key;
      this.background = this.drawBackground(this.dpr, lanes);
    }
    ctx.drawImage(this.background, 0, 0, this.size, this.size);

    const vision = sim.match.enabled && player ? visionSources(sim, player.team) : [];
    if (vision.length > 0) this.drawFog(vision);

    // estructuras
    for (const s of sim.structures) {
      const [x, y] = this.toMap(s.pos.x, s.pos.z);
      const r = s.kind === 'nexus' ? 6 : 4;
      ctx.save();
      ctx.translate(x, y);
      if (s.kind === 'nexus') ctx.rotate(Math.PI / 4);
      ctx.fillStyle = s.alive ? TEAM_CSS[s.team] : 'rgba(90, 100, 110, 0.6)';
      ctx.strokeStyle = s.alive ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 1.2;
      ctx.fillRect(-r, -r, r * 2, r * 2);
      ctx.strokeRect(-r, -r, r * 2, r * 2);
      ctx.restore();
    }

    // minions
    for (const m of sim.minions) {
      if (!m.alive || m.hidden) continue;
      if (vision.length > 0 && m.team !== player!.team && !isVisible(vision, m.pos.x, m.pos.z, m.radius)) continue;
      const [x, y] = this.toMap(m.pos.x, m.pos.z);
      ctx.fillStyle = TEAM_CSS[m.team];
      ctx.fillRect(x - 1.5, y - 1.5, 3, 3);
    }

    // héroes (el jugador al final, encima)
    const heroes = [...sim.heroes].sort((a, b) => Number(a.isPlayer) - Number(b.isPlayer));
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '700 8px Bahnschrift, Segoe UI, sans-serif';
    for (const h of heroes) {
      if (!h.alive || h.hidden) continue;
      if (vision.length > 0 && h.team !== player!.team && !isVisible(vision, h.pos.x, h.pos.z, h.radius)) continue;
      const [x, y] = this.toMap(h.pos.x, h.pos.z);
      const r = h.isPlayer ? 7 : 6;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#0d1621';
      ctx.fill();
      ctx.lineWidth = h.isPlayer ? 2.5 : 2;
      ctx.strokeStyle = h.isPlayer ? '#ffd166' : TEAM_CSS[h.team];
      ctx.stroke();
      ctx.fillStyle = '#e8f2ff';
      ctx.fillText(getHeroDef(h.heroId).displayName[0], x, y + 0.5);
    }

    // recuadro de la cámara: esquinas de la pantalla proyectadas al suelo
    const corners: Array<[number, number]> = [];
    for (const [nx, ny] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      this.ndc.set(nx, ny);
      this.raycaster.setFromCamera(this.ndc, camera);
      if (this.raycaster.ray.intersectPlane(this.ground, this.hit)) corners.push(this.toMap(this.hit.x, this.hit.z));
    }
    if (corners.length === 4) {
      ctx.beginPath();
      corners.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.closePath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    if (player && !player.alive) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fillRect(0, 0, this.size, this.size);
    }
  }

  /** Oscurece el minimapa y abre agujeros suaves en los focos de visión aliados. */
  private drawFog(sources: readonly VisionSource[]): void {
    const ctx = this.fogCtx;
    const center = this.size / 2;
    ctx.clearRect(0, 0, this.size, this.size);
    ctx.save();
    ctx.beginPath();
    ctx.arc(center, center, ARENA_RADIUS * this.scale, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = 'rgba(2, 6, 12, 0.86)';
    ctx.fillRect(0, 0, this.size, this.size);
    ctx.globalCompositeOperation = 'destination-out';
    for (const source of sources) {
      const [x, y] = this.toMap(source.x, source.z);
      const radius = source.radius * this.scale;
      const inner = Math.max(0, radius - 2 * this.scale);
      const gradient = ctx.createRadialGradient(x, y, inner, x, y, radius);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    this.ctx.drawImage(this.fogCanvas, 0, 0, this.size, this.size);
  }

  private eventToWorld(event: MouseEvent): { x: number; z: number } {
    const rect = this.canvas.getBoundingClientRect();
    return this.toWorld(event.clientX - rect.left, event.clientY - rect.top);
  }

  private readonly onMouseDown = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    const world = this.eventToWorld(event);
    if (event.button === 2) this.onMove(world.x, world.z);
    if (event.button === 0) {
      this.leftDown = true;
      this.peek = world;
    }
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (this.leftDown) this.peek = this.eventToWorld(event);
  };

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button === 0 && this.leftDown) {
      this.leftDown = false;
      this.peek = null;
    }
  };
}
